/**
 * Casmani Mega Menu
 * Matches panels to nav links by href or title.
 * On item hover: switches subcollection panel + products panel in the same mega panel.
 *
 * Close logic: single document.mousemove handler checks whether the pointer is
 * inside the "zone" (active trigger <li> OR open panel). This is more reliable
 * than mouseleave/mouseenter chains, which suffer from ordering issues when the
 * trigger <li> extends into the panel area due to CSS transforms.
 */

class CasmaniMegaMenu {
  constructor() {
    this.header = document.getElementById('header-component');
    this.panels = {};
    this.triggerItems = {};
    this.activeId = null;
    this.closeTimer = null;

    if (!this.header) return;

    document.querySelectorAll('[data-mega-panel]').forEach((panel) => {
      const id = panel.dataset.megaPanel;
      if (id) this.panels[id] = panel;
    });

    // Mobile drawer runs regardless of desktop panels
    this.initMobileDrawer();

    if (!Object.keys(this.panels).length) return;

    this.positionContainers();
    window.addEventListener('resize', () => this.positionContainers());
    window.addEventListener('scroll', () => this.positionContainers(), { passive: true });

    document.addEventListener('mousemove', (e) => {
      if (!this.activeId) return;
      // Keep open over the trigger, the panel, OR anywhere on the header bar
      // (moving up onto the header must NOT close it — only hovering a different
      // menu item closes it, which is handled by per-link mouseenter handlers).
      if (this.inZone(e.clientX, e.clientY) || this.inHeader(e.clientX, e.clientY)) {
        this.cancelClose();
      } else if (this.clearlyOutside(e.clientX, e.clientY)) {
        // Pointer has plainly left the menu (e.g. moved down past the panel) —
        // close right away instead of waiting out the grace timer.
        this.cancelClose();
        this.closeAll();
      } else {
        // Only just outside (e.g. above the header) — brief grace.
        this.scheduleClose();
      }
    }, { passive: true });

    this.tryBind();
  }

  // ── Mobile sub-drawers ──────────────────────────────────────────────────────

  initMobileDrawer() {
    const dataEl = document.getElementById('casmani-mobile-nav');
    if (!dataEl) return;

    let triggers;
    try { triggers = JSON.parse(dataEl.textContent); } catch { return; }
    const active = triggers.filter((t) => t.items.length > 0);
    if (!active.length) return;

    this.backIcon = dataEl.dataset.backIcon || '';
    this.placeholderImg = dataEl.dataset.placeholderImg || '';
    const mm = this;

    // Find which trigger matches a drawer link (by href then by visible text)
    const matchTrigger = (link) => active.find((t) => {
      const tHref = mm.normHref(t.link);
      const linkHref = mm.normHref(link.getAttribute('href'));
      if (tHref && linkHref && linkHref === tHref) return true;
      return (link.textContent || '').trim().toLowerCase() === (t.title || '').trim().toLowerCase();
    });

    // Ensure sub-drawers exist in the drawer; add carets to trigger links
    const setup = () => {
      const drawer = document.querySelector('.menu-drawer');
      if (!drawer) return;

      // Sub-drawers sit BELOW the shared green header — expose its height so the
      // header (logo + close) stays visible above the sub-drawer.
      const sharedHeader = drawer.querySelector('.menu-drawer__header');
      if (sharedHeader && sharedHeader.offsetHeight) {
        drawer.style.setProperty('--casmani-sub-top', sharedHeader.offsetHeight + 'px');
      }

      active.forEach((trigger) => {
        // Build sub-drawer if missing
        if (!drawer.querySelector(`[data-casmani-submenu="${trigger.id}"]`)) {
          drawer.appendChild(mm.buildMobileSubDrawer(trigger));
        }

        // Add caret to the matching link if missing
        const link = [...drawer.querySelectorAll('.menu-drawer__menu-item--mainlist')]
          .find((a) => matchTrigger(a) === trigger);
        if (link && !link.querySelector('.casmani-fwd-caret')) {
          link.closest('li')?.classList.add('has-mobile-submenu');
          const caret = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          caret.setAttribute('viewBox', '0 0 14 14');
          caret.setAttribute('fill', 'none');
          caret.setAttribute('aria-hidden', 'true');
          caret.setAttribute('class', 'casmani-fwd-caret');
          caret.style.cssText = 'width:20px;height:20px;flex-shrink:0;opacity:0.5;visibility:visible;overflow:visible;display:block;';
          caret.innerHTML = '<path d="M5 2l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>';
          link.appendChild(caret);
        }
      });
    };

    // Event delegation on document (capture) — survives any Shopify DOM re-render
    document.addEventListener('click', (e) => {
      // ── Open sub-drawer ──
      const link = e.target.closest('.menu-drawer .menu-drawer__menu-item--mainlist');
      if (link) {
        const trigger = matchTrigger(link);
        if (trigger) {
          e.preventDefault();
          e.stopPropagation();
          setup(); // ensure sub-drawer exists (re-creates if Shopify removed it)
          const drawer = link.closest('.menu-drawer');
          const sub = drawer?.querySelector(`[data-casmani-submenu="${trigger.id}"]`);
          if (sub) sub.classList.add('is-open');
          return;
        }
      }

      // ── Back button ──
      const backBtn = e.target.closest('.casmani-back-btn');
      if (backBtn) {
        backBtn.closest('.casmani-mobile-submenu')?.classList.remove('is-open');
        return;
      }

      // ── Close button ──
      const closeBtn = e.target.closest('.casmani-submenu-close');
      if (closeBtn) {
        closeBtn.closest('.casmani-mobile-submenu')?.classList.remove('is-open');
        document.getElementById('Details-menu-drawer-container')?.removeAttribute('open');
        return;
      }
    }, true /* capture — runs before Shopify's bubble handlers */);

    // Run setup once and again each time the drawer is opened
    setup();
    const details = document.getElementById('Details-menu-drawer-container');
    if (details) {
      new MutationObserver(() => {
        if (details.hasAttribute('open')) {
          setTimeout(setup, 80);
        } else {
          // Menu closed via the shared header — reset any open sub-drawer
          document.querySelectorAll('.casmani-mobile-submenu.is-open')
            .forEach((s) => s.classList.remove('is-open'));
        }
      }).observe(details, { attributes: true, attributeFilter: ['open'] });
    }

    // Shopify re-renders the header section (e.g. when the cart count updates
    // via the Section Rendering API) and replaces the drawer DOM with fresh
    // server markup — which has no carets, since carets are JS-injected. The
    // `open`-attribute observer above only re-runs on toggle, so it misses
    // these re-renders and the carets silently vanish. Watch the whole body
    // and re-run setup whenever a fresh drawer (any node containing mainlist
    // links) is inserted. Our own injected nodes are skipped to avoid a
    // feedback loop; setup() is idempotent regardless.
    new MutationObserver((mutations) => {
      const drawerRerendered = mutations.some((m) =>
        [...m.addedNodes].some((n) => {
          if (n.nodeType !== 1) return false;
          if (n.classList?.contains('casmani-fwd-caret') ||
              n.classList?.contains('casmani-mobile-submenu')) return false;
          return n.matches?.('.menu-drawer__menu-item--mainlist') ||
                 !!n.querySelector?.('.menu-drawer__menu-item--mainlist');
        })
      );
      if (drawerRerendered) setup();
    }).observe(document.body, { childList: true, subtree: true });
  }

  // Same markup as snippet 'product-placeholder' (styles live in 'brand-tokens').
  placeholderMarkup() {
    const img = this.placeholderImg
      ? `<img class="cm-ph__img" src="${this.placeholderImg}" alt="" loading="lazy">`
      : '';
    return `<span class="cm-ph">${img}</span>`;
  }

  buildMobileSubDrawer(trigger) {
    const el = document.createElement('div');
    el.className = 'casmani-mobile-submenu';
    el.setAttribute('data-casmani-submenu', trigger.id);

    const isCollections = trigger.kind === 'collections';

    // Items: image rows (collections) or accordions / plain links (products)
    const itemsHtml = trigger.items.map((item) => {
      if (isCollections || item.img) {
        return `<li class="casmani-coll-li">
          <a href="${item.link}" class="casmani-coll-row">
            <span class="casmani-coll-thumb">${item.img ? `<img src="${item.img}" alt="" loading="lazy">` : ''}</span>
            <span class="casmani-coll-label">${item.label}</span>
          </a>
        </li>`;
      }
      const hasSub = item.subItems && item.subItems.length > 0;
      const subHtml = hasSub
        ? `<div class="casmani-sub-wrap"><ul class="casmani-sub-items">${item.subItems.map((s) =>
            `<li><a href="${s.link}" class="casmani-sub-link">
              <span class="casmani-sub-thumb">${s.img ? `<img src="${s.img}" alt="" loading="lazy">` : ''}</span>
              <span class="casmani-sub-label">${s.label}</span>
            </a></li>`).join('')}</ul></div>`
        : '';
      const caretSvg = hasSub
        ? `<svg class="casmani-item-caret" viewBox="0 0 14 14" width="18" height="18" fill="none" aria-hidden="true"><path d="M2 5l5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`
        : '';

      const tag = hasSub ? 'button' : 'a';
      const href = !hasSub ? `href="${item.link}"` : '';
      return `<li class="${hasSub ? 'casmani-has-children' : ''}">
        <${tag} ${href} class="casmani-item-link">
          <span>${item.label}</span>${caretSvg}
        </${tag}>
        ${subHtml}
      </li>`;
    }).join('');

    // "View all" button — collections layout only
    const seeAllHtml = isCollections
      ? `<a class="casmani-coll-seeall" href="${trigger.link || '#'}">${trigger.seeAllLabel || 'View all'}</a>`
      : '';

    // Product carousel — products layout only
    const badgeClass = { Sale: 'sale', Nou: 'new', Top: 'top' };
    const products = trigger.products || [];
    const carouselHtml = (!isCollections && products.length)
      ? `<div class="casmani-prod-carousel">${products.map((p) => `
          <a class="casmani-prod-card" href="${p.url}">
            <span class="casmani-prod-imgwrap">
              ${p.img ? `<img src="${p.img}" alt="" loading="lazy">` : this.placeholderMarkup()}
              ${p.badge ? `<span class="casmani-prod-badge casmani-prod-badge--${badgeClass[p.badge] || 'sale'}">${p.badge}</span>` : ''}
            </span>
            <span class="casmani-prod-title">${p.title}</span>
            <span class="casmani-prod-prices">
              <span class="casmani-prod-price">${p.price}</span>
              ${p.compare ? `<span class="casmani-prod-compare">${p.compare}</span>` : ''}
            </span>
          </a>`).join('')}</div>`
      : '';

    el.innerHTML = `
      <div class="casmani-submenu-header">
        <button class="casmani-back-btn" aria-label="Back">
          ${this.backIcon ? `<img class="casmani-back-icon" src="${this.backIcon}" width="20" height="20" alt="" aria-hidden="true">` : ''}
          ${trigger.title}
        </button>
      </div>
      <ul class="casmani-submenu-list${isCollections ? ' casmani-submenu-list--collections' : ''}">${itemsHtml}</ul>
      ${seeAllHtml}
      ${carouselHtml}
    `;

    // Accordion for items with sub-items
    el.querySelectorAll('.casmani-has-children > .casmani-item-link').forEach((btn) => {
      btn.addEventListener('click', () => {
        const li = btn.closest('li');
        const isOpen = li.classList.contains('casmani-item-open');
        el.querySelectorAll('.casmani-item-open').forEach((o) => o.classList.remove('casmani-item-open'));
        if (!isOpen) li.classList.add('casmani-item-open');
      });
    });

    return el;
  }

  // ────────────────────────────────────────────────────────────────────────────

  // Normalises an href for comparison: drops origin, query/hash and trailing
  // slash, then strips the storefront locale root ('/ru/', '/en/'…).
  // Nav hrefs are ALWAYS localised by Shopify, while a panel's link comes from
  // a theme setting and may be stored either raw ('/collections/all') or as a
  // localised resource link ('/ru/collections') — comparing them verbatim
  // breaks the menu on every non-default language.
  normHref(href) {
    let s = (href || '').trim();
    if (!s || s.charAt(0) === '#') return '';
    if (/^https?:\/\//i.test(s)) {
      try { s = new URL(s).pathname; } catch { /* leave as typed */ }
    }
    s = s.split('?')[0].split('#')[0].replace(/\/+$/, '');
    const root = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/+$/, '');
    if (root && (s === root || s.indexOf(root + '/') === 0)) s = s.slice(root.length);
    return s || '/';
  }

  // Returns true when the pointer is over the active trigger <li> OR the open panel.
  inZone(x, y) {
    const trigger = this.triggerItems[this.activeId];
    if (trigger) {
      const r = trigger.getBoundingClientRect();
      if (y >= r.top && y <= r.bottom && x >= r.left && x <= r.right) return true;
    }
    const panel = this.panels[this.activeId];
    if (panel) {
      const r = panel.getBoundingClientRect();
      if (y >= r.top && y <= r.bottom && x >= r.left && x <= r.right) return true;
    }
    return false;
  }

  // True when the pointer is plainly away from the menu — below the panel's bottom
  // edge or beyond its sides — as opposed to hovering the thin seam between the
  // trigger and the panel (which warrants a brief grace delay, not an instant close).
  clearlyOutside(x, y) {
    const panel = this.panels[this.activeId];
    if (!panel) return true;
    const r = panel.getBoundingClientRect();
    const m = 4; // small tolerance around the panel edges
    if (y > r.bottom + m) return true;            // moved down past the panel
    if (x < r.left - m || x > r.right + m) return true; // moved out the sides
    return false;
  }

  // True when the pointer is anywhere over the header bar — a safe area that keeps
  // the current panel open (only hovering a different menu item dismisses it).
  inHeader(x, y) {
    if (!this.header) return false;
    const r = this.header.getBoundingClientRect();
    return y >= r.top && y <= r.bottom && x >= r.left && x <= r.right;
  }

  positionContainers() {
    const rect = this.header.getBoundingClientRect();
    document.querySelectorAll('[data-mega-menu-section]').forEach((el) => {
      el.style.top = rect.bottom + 'px';
    });
  }

  tryBind() {
    const sel = '.menu-list__list-item:not([slot="overflow"]) > .menu-list__link';

    const bind = () => {
      const links = document.querySelectorAll(sel);
      if (!links.length) return false;
      this.bindLinks(links);
      this.bindItemHovers();
      return true;
    };

    // Keep observer running permanently to catch header-menu hydration re-renders.
    // Filter out mutations caused by our own chevron injections to avoid feedback loops.
    const observer = new MutationObserver((mutations) => {
      const isOurMutation = mutations.every((m) =>
        [...m.addedNodes].every((n) => n.classList && n.classList.contains('mega-menu__chevron'))
      );
      if (!isOurMutation) bind();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    if (!bind()) {
      let elapsed = 0;
      const poll = setInterval(() => {
        elapsed += 100;
        if (bind() || elapsed >= 5000) clearInterval(poll);
      }, 100);
    }

    this.bindOutside();
  }

  bindLinks(links) {
    links.forEach((link) => {
      const titleEl = link.querySelector('.menu-list__link-title');
      const linkText = (titleEl || link).textContent.trim().toLowerCase();
      const linkHref = this.normHref(link.getAttribute('href'));

      Object.entries(this.panels).forEach(([id, panel]) => {
        const panelTitle = (panel.dataset.megaTitle || '').trim().toLowerCase();
        const panelLink  = this.normHref(panel.dataset.megaLink);

        const byHref  = panelLink  && linkHref && linkHref === panelLink;
        const byTitle = panelTitle && linkText === panelTitle && (!panelLink || !linkHref);

        if (!byHref && !byTitle) return;

        const chevronExists = !!link.querySelector('.mega-menu__chevron');
        if (link.dataset.megaBound === id && chevronExists) return;
        link.dataset.megaBound = id;

        link.addEventListener('click', (e) => e.preventDefault());

        const item = link.closest('.menu-list__list-item') || link.parentElement;
        item.classList.add('has-mega-menu');
        this.triggerItems[id] = item;

        // Inject chevron into <a data-skip-node-update> so Shopify's DOM patcher skips it.
        if (!link.querySelector('.mega-menu__chevron')) {
          const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          chevron.setAttribute('class', 'mega-menu__chevron');
          chevron.setAttribute('viewBox', '0 0 12 8');
          chevron.setAttribute('width', '12');
          chevron.setAttribute('height', '8');
          chevron.setAttribute('aria-hidden', 'true');
          chevron.setAttribute('fill', 'none');
          chevron.innerHTML = '<path d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>';
          link.appendChild(chevron);
        }

        // mouseenter on the trigger opens the panel.
        // Close is handled entirely by the mousemove zone-check above — no mouseleave needed.
        item.addEventListener('mouseenter', () => this.open(id));
      });

      // Top-level links that are NOT mega triggers: hovering one dismisses any open
      // mega panel. (Hovering another mega trigger switches via its own handler above;
      // hovering header chrome keeps the panel open via the inHeader zone-check.)
      if (!link.dataset.megaBound && !link.dataset.megaCloseBound) {
        link.dataset.megaCloseBound = '1';
        const plainItem = link.closest('.menu-list__list-item') || link.parentElement;
        plainItem.addEventListener('mouseenter', () => this.closeAll());
      }
    });
  }

  bindItemHovers() {
    document.querySelectorAll('.mega-menu__item[data-item-id]:not([data-hover-bound])').forEach((item) => {
      item.dataset.hoverBound = '1';
      item.addEventListener('mouseenter', () => {
        const panel = item.closest('[data-mega-panel]');
        if (!panel) return;
        this.activateItem(panel, item.dataset.itemId);
      });
    });

    document.querySelectorAll('.mega-menu__item[data-simple-link]:not([data-hover-bound])').forEach((item) => {
      item.dataset.hoverBound = '1';
      item.addEventListener('mouseenter', () => {
        const panel = item.closest('[data-mega-panel]');
        if (!panel) return;
        panel.querySelectorAll('.mega-menu__item').forEach((i) => i.classList.remove('is-active'));
        panel.querySelectorAll('[data-sub-panel]').forEach((sp) => sp.classList.remove('is-active'));
        panel.querySelectorAll('[data-products-panel]').forEach((pp) => pp.classList.remove('is-active'));
      });
    });
  }

  activateItem(panel, itemId) {
    panel.querySelectorAll('.mega-menu__item').forEach((i) => i.classList.remove('is-active'));
    const activeItem = panel.querySelector(`.mega-menu__item[data-item-id="${itemId}"]`);
    if (activeItem) activeItem.classList.add('is-active');

    panel.querySelectorAll('[data-sub-panel]').forEach((sp) => sp.classList.remove('is-active'));
    const sub = panel.querySelector(`[data-sub-panel="${itemId}"]`);
    if (sub) sub.classList.add('is-active');

    panel.querySelectorAll('[data-products-panel]').forEach((pp) => pp.classList.remove('is-active'));
    const products = panel.querySelector(`[data-products-panel="${itemId}"]`);
    if (products) products.classList.add('is-active');

    // Lock the panel height at its maximum to prevent shrinking when
    // switching to an item with less content.
    const inner = panel.querySelector('.mega-menu__panel-inner');
    if (inner) {
      const h = inner.offsetHeight;
      const locked = parseInt(inner.style.minHeight) || 0;
      if (h > locked) inner.style.minHeight = h + 'px';
    }
  }

  // ── Scroll lock while a panel is open ──
  // Reuses the theme's own convention: html[scroll-lock] { overflow: hidden }.
  lockScroll() {
    const html = document.documentElement;
    if (html.hasAttribute('scroll-lock')) return;
    // Compensate for the disappearing scrollbar so the page doesn't shift.
    const scrollbarWidth = window.innerWidth - html.clientWidth;
    if (scrollbarWidth > 0) document.body.style.paddingRight = scrollbarWidth + 'px';
    html.setAttribute('scroll-lock', '');
  }

  unlockScroll() {
    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.paddingRight = '';
  }

  open(id) {
    this.cancelClose();
    this.positionContainers();

    // If switching between triggers: hide old panel instantly (no cross-fade
    // flicker where the taller panel's bottom shows under the shorter one).
    if (this.activeId && this.activeId !== id) {
      this.switchPanel(this.activeId);
    }

    const panel = this.panels[id];
    if (!panel) return;

    panel.hidden = false;
    panel.getBoundingClientRect(); // force reflow so transition plays
    panel.classList.add('is-open');
    this.activeId = id;
    this.lockScroll();
    if (this.triggerItems[id]) this.triggerItems[id].classList.add('mega-open');

    const firstItem = panel.querySelector('.mega-menu__item[data-item-id]');
    if (firstItem) this.activateItem(panel, firstItem.dataset.itemId);
  }

  // Instant hide when switching triggers — no fade to avoid height-flicker.
  switchPanel(id) {
    const panel = this.panels[id];
    if (!panel) return;
    panel.classList.remove('is-open');
    panel.hidden = true;
    if (this.triggerItems[id]) this.triggerItems[id].classList.remove('mega-open');
    const inner = panel.querySelector('.mega-menu__panel-inner');
    if (inner) inner.style.minHeight = '';
    this.activeId = null;
  }

  scheduleClose() {
    if (this.closeTimer) return; // already scheduled — don't restart
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      this.closeAll();
    }, 120);
  }

  cancelClose() {
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  closeAll() {
    if (this.activeId) { this.closeImmediate(this.activeId); this.activeId = null; }
  }

  // Animated close — used when user leaves the nav zone entirely.
  closeImmediate(id) {
    const panel = this.panels[id];
    if (!panel) return;
    this.unlockScroll();
    panel.classList.remove('is-open');
    if (this.triggerItems[id]) this.triggerItems[id].classList.remove('mega-open');
    const inner = panel.querySelector('.mega-menu__panel-inner');
    if (inner) inner.style.minHeight = '';
    const done = () => { if (!panel.classList.contains('is-open')) panel.hidden = true; };
    panel.addEventListener('transitionend', done, { once: true });
  }

  bindOutside() {
    document.addEventListener('click', (e) => {
      if (!this.header.contains(e.target) && !e.target.closest('[data-mega-panel]')) {
        this.closeAll();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeAll();
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CasmaniMegaMenu());
} else {
  new CasmaniMegaMenu();
}
